import type { IntegrationId } from '../api/client';

/**
 * Plain-language integration copy (Story #47, SRS FR-10.3). Kept in the frontend
 * (i18n-ready) — the backend is authoritative for state only.
 *
 * `INTEGRATION_ASSURANCE` states the FR-10.2 / FR-12.6 guarantee that integrations
 * never access source code, repository contents, or the production database, and
 * no blurb overstates an integration's access. The `integrationCopy.test.ts`
 * completeness test enforces this.
 */

/** One global guarantee shown prominently on the settings page. */
export const INTEGRATION_ASSURANCE =
  'TestCraft will never access your source code, repository contents, or production database. Each integration below uses only the access it describes.';

export interface IntegrationCopy {
  /** What access the integration uses, in plain language. */
  access: string;
  /** How data is used, in plain language. */
  dataUse: string;
}

/** Per-integration access + data-use blurbs. `Record<IntegrationId, …>` makes a
 * missing entry a compile error (guarantees AC-3 coverage). */
export const INTEGRATION_COPY: Record<IntegrationId, IntegrationCopy> = {
  jira: {
    access:
      'Permission to create and update issues in the Jira project you choose. No access to code, wikis, or admin settings.',
    dataUse:
      'When you confirm a defect, TestCraft opens or updates a Jira issue with the summary, steps, and evidence links you approve, and reads issue status to keep journey health in sync.',
  },
  github: {
    access:
      'Issue read/write on the repositories you select — scoped to Issues only. It never reads repository contents, source code, pull requests, or Actions.',
    dataUse:
      'Confirmed defects become GitHub issues containing the summary, reproduction steps, and evidence links you approve; issue state is read back to reflect resolution in journey health.',
  },
  gitlab: {
    access:
      'Permission to create and update issues in the GitLab projects you select. No access to repositories, source code, or CI/CD.',
    dataUse:
      'A confirmed defect creates or updates a GitLab issue with the details and evidence you approve, and TestCraft reads the issue’s state to keep health up to date.',
  },
  linear: {
    access:
      'Permission to create and update issues in the Linear team you choose. No access to any connected code repositories.',
    dataUse:
      'Confirmed defects sync to Linear as issues with the summary, steps, and evidence links you approve; TestCraft reads status to reflect fixes in journey health.',
  },
  shopify: {
    access:
      'Read-only access to your storefront’s public catalog and theme structure to recognize shopping journeys. No access to orders, customers, payments, or the Shopify admin.',
    dataUse:
      'TestCraft reads product and page metadata to map checkout and browsing journeys. It never reads customer, order, or payment data, and never places real orders.',
  },
};
